import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarClock, Gift } from 'lucide-react';
import { formatReset } from '../../lib/aiHosted';

/** Tone by remaining share: plenty → primary, low → sun, empty → pop. */
export function meterTone(credits) {
  if (!credits.total) return 'primary';
  if (credits.remaining <= 0) return 'pop';
  if (credits.remaining / credits.total <= 0.2) return 'sun';
  return 'primary';
}

const BAR = { primary: 'bg-primary', sun: 'bg-sun', pop: 'bg-pop' };

/** "12 of 30 AI credits left this month" */
export function creditsSentence(credits) {
  return `${credits.remaining} of ${credits.total} AI credit${credits.total === 1 ? '' : 's'} left ${credits.periodLabel}`;
}

/**
 * Usage meter for hosted AI credits: big remaining figure, bar, reset and daily cap.
 * `credits` is `creditsView(...)` from lib/aiHosted.
 */
export default function AiUsageMeter({ credits, loading = false, size = 'md', className = '' }) {
  const reduce = useReducedMotion();
  if (loading || !credits || !credits.known) {
    return (
      <div className={`space-y-2 ${className}`} aria-busy="true" aria-label="Loading AI credits">
        <span className="skeleton block h-6 w-40 rounded-lg" />
        <span className="skeleton block h-2.5 w-full rounded-full" />
        <span className="skeleton block h-3 w-28 rounded-full" />
      </div>
    );
  }
  const tone = meterTone(credits);
  const usedPct = credits.pct;
  const reset = formatReset(credits.resetAt);
  const big = size === 'lg';
  return (
    <div className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-ink">
          <span className={`font-display font-extrabold tabular-nums tracking-tight ${big ? 'text-4xl' : 'text-2xl'}`}>{credits.remaining}</span>
          <span className="ml-1 font-semibold text-muted">
            of {credits.total} credit{credits.total === 1 ? '' : 's'} left {credits.periodLabel}
          </span>
        </p>
        {reset && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> {reset}
          </span>
        )}
      </div>
      <div
        className={`mt-2 overflow-hidden rounded-full bg-line/20 ${big ? 'h-3' : 'h-2.5'}`}
        role="meter"
        aria-label="AI credits used"
        aria-valuemin={0}
        aria-valuemax={credits.total || 0}
        aria-valuenow={Math.max(0, credits.total - credits.remaining)}
        aria-valuetext={creditsSentence(credits)}
      >
        <motion.span
          className={`block h-full origin-left rounded-full ${BAR[tone]}`}
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: usedPct / 100 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        <span className="tabular-nums">{Math.max(0, credits.total - credits.remaining)} used</span>
        {credits.dailyMax > 0 && credits.period !== 'day' && (
          <span className="tabular-nums">
            Daily cap: {credits.dailyLeft} of {credits.dailyMax} left today
          </span>
        )}
        {credits.bonus > 0 && (
          <span className="inline-flex items-center gap-1 font-semibold text-ink">
            <Gift className="h-3.5 w-3.5 text-pop" aria-hidden="true" /> includes {credits.bonus} bonus
          </span>
        )}
      </div>
    </div>
  );
}
